import { useLayoutEffect, useRef, useState, type FunctionComponent, type ReactNode } from "react";
import { edgeFade, revealScrollLeft, type EdgeFade } from "./edgeFade";

/** The fade's width, px; the current item is kept this far clear of a faded edge. */
const FADE_PX = 24;

/** The current item: the top bar's page (aria-current), a segmented control's chosen segment (Radix data-state). */
const CURRENT = '[aria-current="page"],[data-state="on"]';

// The mask fades the content itself, so whatever is behind the row (the landing hero, frosted glass, a well) shows
// through. Masks carry alpha only: black is opaque, transparent is gone.
const MASK: Record<EdgeFade, string> = {
  none: "",
  start: "[mask-image:linear-gradient(to_right,transparent,black_24px)]",
  end: "[mask-image:linear-gradient(to_left,transparent,black_24px)]",
  both: "[mask-image:linear-gradient(to_right,transparent,black_24px,black_calc(100%_-_24px),transparent)]",
};

interface Props {
  readonly children: ReactNode;
  /** Layout for the row itself (flex, gap, height). */
  readonly className?: string;
  /** Re-reveal the current item when this changes: the route, the chosen value. */
  readonly revealKey?: string;
}

/** Scroll the current item into view, clear of the fades. */
function reveal(el: HTMLElement) {
  const item = el.querySelector<HTMLElement>(CURRENT);
  if (!item) return;
  const box = el.getBoundingClientRect();
  const r = item.getBoundingClientRect();
  const itemLeft = r.left - box.left + el.scrollLeft;
  el.scrollLeft = revealScrollLeft({
    scrollLeft: el.scrollLeft, scrollWidth: el.scrollWidth, clientWidth: el.clientWidth,
    itemLeft, itemRight: itemLeft + r.width, inset: FADE_PX,
  });
}

/**
 * A row that scrolls sideways inside its box instead of running past it (#262): the top bar's tabs on a phone, a
 * long segmented strip in a narrow tile. The edge its content runs past fades (edgeFade.ts), and the current item is
 * scrolled into view: when it mounts, when `revealKey` changes, once the web fonts are in (the swap from the fallback
 * face changes every item's width), and when the box itself changes width (the top bar's Sign In button replaces a
 * narrower placeholder once Clerk loads). A scroll the reader made is left alone otherwise. When everything fits it
 * draws nothing extra.
 */
export const EdgeScroller: FunctionComponent<Props> = ({ children, className = "", revealKey }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [fade, setFade] = useState<EdgeFade>("none");

  // Follow the scroll and every size change: the box's, and each child's (a count loads, a segment is added).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setFade(edgeFade(el));
    update();
    el.addEventListener("scroll", update, { passive: true });
    let boxWidth = el.clientWidth;
    const sizes = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => {
      if (el.clientWidth !== boxWidth) {
        boxWidth = el.clientWidth;
        reveal(el);
      }
      update();
    });
    const observe = () => {
      if (!sizes) return;
      sizes.disconnect();
      sizes.observe(el);
      for (const c of Array.from(el.children)) sizes.observe(c);
    };
    observe();
    const kids = typeof MutationObserver === "undefined" ? null : new MutationObserver(() => { observe(); update(); });
    kids?.observe(el, { childList: true });
    return () => {
      el.removeEventListener("scroll", update);
      sizes?.disconnect();
      kids?.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    reveal(el);
    let live = true;
    document.fonts?.ready.then(() => { if (live) reveal(el); }).catch(() => undefined);
    return () => { live = false; };
  }, [revealKey]);

  return (
    <div ref={ref} data-fade={fade} className={`no-scrollbar overflow-x-auto overscroll-x-contain ${MASK[fade]} ${className}`.trim()}>
      {children}
    </div>
  );
};
